import { useCallback, useState } from "react";

/**
 * @typedef {Object} useTableLayoutReturn
 * @property {Object} stickyLefts - An object mapping column indices to their left offset values for sticky positioning.
 * @property {Function} calculateStickyColumnsOffset - Function to recalculate the left offsets of sticky columns.
 * @property {Function} updateTableLayout - Function to update the table layout, including width checks and sticky column offsets.
 */

/**
 * Custom hook to manage table layout, including sticky columns and width adjustments.
 * To get the correct expected behavior, ensure the 'columnResizeMode' property of the
 * DataTable is set to 'expand'.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {React.Ref} params.tableRef - Ref object pointing to the table component.
 * @return {useTableLayoutReturn} - Object containing stickyLefts, calculateStickyColumnsOffset, and updateTableLayout.
 */
export const useTableLayout = ({ tableRef }) => {
  const [stickyLefts, setStickyLefts] = useState({});

  const checkTableWidth = useCallback(() => {
    const root = tableRef.current?.getElement?.() ?? tableRef.current;

    if (!root) return;

    const wrapperEl = root.querySelector(".p-datatable-wrapper");
    const tableEl = root.querySelector(".p-datatable-table");

    if (!wrapperEl || !tableEl) return;

    const wrapperWidth = wrapperEl.getBoundingClientRect().width;
    const tableWidth = tableEl.getBoundingClientRect().width;

    if (tableWidth < wrapperWidth) {
      // If table is smaller than wrapper, set table width to 100%
      // This prevents extra space on the right side due to 'expand'
      // mode column resizing
      tableEl.style.minWidth = "100%";
      tableEl.style.width = "100%";
    }
  }, [tableRef]);

  const calculateStickyColumnsOffset = useCallback(() => {
    const root = tableRef.current?.getElement?.() ?? tableRef.current;

    if (!root) return;

    const ths = root.querySelectorAll(".p-datatable-thead > tr > th");

    let acc = 0;
    const newLefts = {};

    ths.forEach((th, index) => {
      newLefts[index] = acc;
      acc += th.getBoundingClientRect().width;
    });

    setStickyLefts(newLefts);
  }, [tableRef]);

  const updateTableLayout = useCallback(() => {
    checkTableWidth();
    calculateStickyColumnsOffset();
  }, [checkTableWidth, calculateStickyColumnsOffset]);

  return { stickyLefts, calculateStickyColumnsOffset, updateTableLayout };
};
